import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, Ship, Handshake, MessagesSquare, ArrowUpRight } from "lucide-react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import PageHero from "../components/site/PageHero";
import { Field, SuccessPanel, useEnquiryForm, inputCls } from "../components/forms/fields";
import { FadeUp, SectionHead } from "../components/site/Reveal";
import { DisclaimerBox } from "../components/site/ContentBlocks";
import { CONTACT_CHANNELS, FINAL_DISCLAIMER } from "../data/content";

const OPTIONS = [
    { id: "healthcare", label: "Healthcare or Medical Tourism", icon: Stethoscope, to: "/healthcare-enquiry", testid: "contact-opt-healthcare" },
    { id: "trading", label: "International Trading", icon: Ship, to: "/trading-enquiry", testid: "contact-opt-trading" },
    { id: "partnership", label: "Business Partnership", icon: Handshake, to: "/partnership-enquiry", testid: "contact-opt-partnership" },
    { id: "sourcing", label: "Sourcing Products from Vietnam", icon: Ship, to: "/vietnam-sourcing-enquiry", testid: "contact-opt-sourcing" },
];

export default function Contact() {
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const { submitting, reference, submit } = useEnquiryForm("general");
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = (e) => {
        e.preventDefault();
        submit(form, null, true);
    };

    return (
        <div className="grain bg-navy-deep" data-testid="contact-page">
            <Header />
            <main>
                <PageHero
                    seo={{
                        title: "Contact Viet Medica Ltd | Healthcare, Trading & Sourcing Enquiries",
                        description:
                            "Start the right conversation — healthcare, international trading, Vietnam sourcing, partnership or general enquiries directed to the appropriate Viet Medica specialist.",
                        path: "/contact",
                    }}
                    crumbs={[{ label: "Contact" }]}
                    eyebrow="Contact"
                    title="Start the Right"
                    titleEm="Conversation."
                    copy="Whether you are planning healthcare abroad, developing an international partnership or seeking a reliable trade connection, our team will direct your enquiry to the appropriate specialist."
                />

                <section className="bg-navy-deep py-20 lg:py-28" data-testid="contact-router">
                    <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                        <SectionHead eyebrow="How Can We Help?" title="Choose Your Enquiry Type" />
                        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            {OPTIONS.map((o) => (
                                <button
                                    key={o.id}
                                    type="button"
                                    data-testid={o.testid}
                                    onClick={() => navigate(o.to)}
                                    className="group relative border border-white/10 p-7 text-left transition-colors duration-500 hover:border-gold/60 hover:bg-navy-surface/60"
                                >
                                    <ArrowUpRight size={16} className="absolute right-6 top-6 text-slate-500 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" aria-hidden="true" />
                                    <o.icon size={22} className="text-gold" aria-hidden="true" />
                                    <span className="mt-5 block font-serif text-xl font-light leading-snug text-ivory lg:text-2xl">{o.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="bg-ivory py-20 text-navy-deep lg:py-28" data-testid="contact-general">
                    <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-14 px-5 lg:grid-cols-2 lg:px-10">
                        <div>
                            <SectionHead light eyebrow="General Enquiry" title="Send a Message" copy="For anything else, send a short message and our team will route it appropriately." />
                            <div className="mt-10">
                                {reference ? (
                                    <SuccessPanel reference={reference} categoryLabel="general" />
                                ) : (
                                    <FadeUp>
                                        <form onSubmit={onSubmit} className="space-y-5 border border-navy-deep/10 bg-white p-7 lg:p-9" data-testid="general-enquiry-form">
                                            <Field label="Name" htmlFor="gn-name" required>
                                                <input id="gn-name" data-testid="gn-name" required className={`${inputCls} !border-navy-deep/20 !bg-ivory !text-navy-deep`} value={form.name} onChange={set("name")} />
                                            </Field>
                                            <Field label="Email" htmlFor="gn-email" required>
                                                <input id="gn-email" data-testid="gn-email" required type="email" className={`${inputCls} !border-navy-deep/20 !bg-ivory !text-navy-deep`} value={form.email} onChange={set("email")} />
                                            </Field>
                                            <Field label="Message" htmlFor="gn-message" required>
                                                <textarea id="gn-message" data-testid="gn-message" required rows={4} className={`${inputCls} !border-navy-deep/20 !bg-ivory !text-navy-deep`} value={form.message} onChange={set("message")} />
                                            </Field>
                                            <button
                                                type="submit"
                                                disabled={submitting}
                                                data-testid="gn-submit"
                                                className="w-full bg-navy-deep px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-ivory transition-colors hover:bg-gold hover:text-navy-deep disabled:opacity-50"
                                            >
                                                {submitting ? "Sending…" : "Send Message"}
                                            </button>
                                        </form>
                                    </FadeUp>
                                )}
                            </div>
                        </div>

                        <div>
                            <SectionHead light eyebrow="Direct Contact" title="Company Contact Details" />
                            <FadeUp delay={0.1}>
                                <div className="mt-10 border border-navy-deep/10 bg-white p-8 lg:p-10" data-testid="contact-channels">
                                    {CONTACT_CHANNELS.map((c) => (
                                        <div
                                            key={c.id}
                                            data-testid={`contact-field-${c.id}`}
                                            className="flex flex-col gap-1 border-t border-navy-deep/10 py-4 first:border-t-0 sm:flex-row sm:items-center sm:justify-between"
                                        >
                                            <span className="text-sm font-semibold text-navy-deep/85">{c.label}</span>
                                            {c.value ? (
                                                c.href ? (
                                                    <a
                                                        href={c.href}
                                                        data-testid={`contact-link-${c.id}`}
                                                        className="text-sm font-medium text-navy-deep transition-colors hover:text-gold sm:text-right"
                                                    >
                                                        {c.value}
                                                    </a>
                                                ) : (
                                                    <span className="text-sm text-navy-deep/80 sm:text-right">{c.value}</span>
                                                )
                                            ) : (
                                                <span className="text-[11px] uppercase tracking-[0.22em] text-navy-deep/40">To be confirmed</span>
                                            )}
                                        </div>
                                    ))}
                                    <p className="mt-6 border-t border-navy-deep/10 pt-6 text-xs leading-relaxed text-navy-deep/50">
                                        Reach us directly using the details above, or through the enquiry forms —
                                        every enquiry is routed to the appropriate specialist team.
                                    </p>
                                </div>
                            </FadeUp>
                        </div>
                    </div>
                </section>

                <section className="bg-navy-deep py-16 lg:py-20">
                    <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                        <DisclaimerBox text={FINAL_DISCLAIMER} testid="contact-final-disclaimer" />
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
